import { useEffect, useRef, useState, createContext, useContext } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Loader2, MessageCircle, Music, Pause, Play, Plus, Send, ShoppingCart, Trash2, X } from "lucide-react";
import { API, api, fileUrl, formatApiErrorDetail } from "@/lib/api";
import { usePlayer } from "@/context/PlayerContext";
import { useCustomerAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const getSid = () => {
  let sid = localStorage.getItem("chat_sid");
  if (!sid) {
    sid = crypto.randomUUID();
    localStorage.setItem("chat_sid", sid);
  }
  return sid;
};

const QUICK_PROMPTS = [
  { label: "Beat pricing", prompt: "How much are your beat leases, and what's the difference between them?" },
  { label: "Book a session", prompt: "How do I book a recording session at Blackroom Studios?" },
  { label: "Mixing rates", prompt: "What are your mixing and mastering rates?" },
  { label: "Music video", prompt: "What do you charge for music videos?" },
];

const LEAD_TOPICS = [
  { topic: "mixing", match: /\b(mix|mixing|master|mastering|remaster)\b/i, label: "mixing / mastering" },
  { topic: "exclusive", match: /\b(exclusive|custom beat|buyout|full rights|50\/50)\b/i, label: "custom / exclusive beat" },
];

const detectLeadTopic = (text) => {
  for (const t of LEAD_TOPICS) if (t.match.test(text)) return t;
  return null;
};

const BEAT_INTENT = /\b(beat|beats|type\s*beat|instrumental|bpm|trap|r\s*&\s*b|rnb|drill|melodic|recommend|suggest|vibe)\b/i;
const BEAT_TOKEN_GLOBAL = /\[\[beat:([^\]|]+)\|/g;

const extractBeatIds = (text) => {
  const ids = [];
  let m;
  while ((m = BEAT_TOKEN_GLOBAL.exec(text)) !== null) ids.push(m[1].trim());
  return ids;
};

// --- Mini cart (chat-local, persisted) --------------------------------------
const CART_KEY = "chat_cart_v1";

const loadCart = () => {
  try {
    const raw = localStorage.getItem(CART_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const saveCart = (items) => {
  try {
    localStorage.setItem(CART_KEY, JSON.stringify(items));
  } catch {
    /* storage full / disabled */
  }
};

const useChatCart = () => {
  const [items, setItems] = useState(loadCart);
  const { user } = useCustomerAuth() || {};
  const authedId = user && user.user_id ? user.user_id : null;
  const [serverReady, setServerReady] = useState(false);
  const lastSyncedRef = useRef("");

  // Pull server cart on sign-in; merge union-of-rows then sync back.
  useEffect(() => {
    if (!authedId) {
      setServerReady(true);
      return;
    }
    // Block PUT until we've hydrated — otherwise a debounced PUT with the pre-hydrate
    // local cart (often []) can race ahead of the GET and wipe the server copy.
    setServerReady(false);
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/account/cart");
        if (cancelled) return;
        const serverItems = Array.isArray(data?.items) ? data.items : [];
        setItems((local) => {
          const key = (i) => `${i.beat_id}:${i.tier}`;
          const seen = new Set();
          const merged = [];
          for (const it of [...serverItems, ...local]) {
            const k = key(it);
            if (!seen.has(k)) {
              seen.add(k);
              merged.push(it);
            }
          }
          lastSyncedRef.current = JSON.stringify(merged);
          return merged;
        });
      } catch {
        /* non-fatal — stay on local */
      } finally {
        if (!cancelled) setServerReady(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [authedId]);

  // Persist to localStorage every change.
  useEffect(() => saveCart(items), [items]);

  // Debounced PUT to server when authed and ready.
  useEffect(() => {
    if (!authedId || !serverReady) return;
    const snapshot = JSON.stringify(items);
    if (snapshot === lastSyncedRef.current) return;
    const t = setTimeout(() => {
      api.put("/account/cart", { items }).then(() => {
        lastSyncedRef.current = snapshot;
      }).catch(() => {});
    }, 600);
    return () => clearTimeout(t);
  }, [items, authedId, serverReady]);

  const addItem = (entry) =>
    setItems((prev) => {
      if (prev.some((p) => p.beat_id === entry.beat_id && p.tier === entry.tier)) return prev;
      const idx = prev.findIndex((p) => p.beat_id === entry.beat_id);
      if (idx >= 0) {
        const copy = [...prev];
        copy[idx] = entry;
        return copy;
      }
      return [...prev, entry];
    });
  const removeItem = (beat_id, tier) =>
    setItems((prev) => prev.filter((p) => !(p.beat_id === beat_id && p.tier === tier)));
  const switchTier = (beat_id, nextTier) =>
    setItems((prev) =>
      prev.map((p) => {
        if (p.beat_id !== beat_id) return p;
        const newPrice = Number(p.prices?.[nextTier] || 0);
        if (!newPrice) return p;
        return { ...p, tier: nextTier, tier_label: tierLabel(nextTier), price: newPrice };
      })
    );
  const clear = () => setItems([]);
  const has = (beat_id, tier) => items.some((p) => p.beat_id === beat_id && p.tier === tier);
  const total = items.reduce((s, i) => s + Number(i.price || 0), 0);
  return { items, addItem, removeItem, switchTier, clear, has, total, signedIn: !!authedId };
};

const CartCtx = createContext(null);
const useCart = () => useContext(CartCtx);

const tierLabel = (tier) => (tier === "wav" ? "Premium" : tier === "exclusive" ? "Exclusive" : "MP3 Lease");

const beatToCartEntry = (beat, tier = "mp3") => ({
  beat_id: beat.id,
  tier,
  title: beat.title,
  cover_url: beat.cover_url,
  price: Number(beat.prices?.[tier] || 0),
  tier_label: tierLabel(tier),
  prices: {
    mp3: Number(beat.prices?.mp3 || 0),
    wav: Number(beat.prices?.wav || 0),
  },
});

// Grammar: [[beat:ID|Title]] and [Label](/path) — rendered as beat chip or in-app link.
const TOKEN = /(\[\[beat:[^\]|]+\|[^\]]+\]\])|(\[[^\]]+\]\(\/[\w\-/]*\))/g;

const BeatChip = ({ beatId, title, onClose }) => {
  const { openLicense, playBeat, toggle, isCurrent, playing } = usePlayer();
  const cart = useCart();
  const [busy, setBusy] = useState(false);
  const [beat, setBeat] = useState(null);

  const isActive = beat && isCurrent(beat);
  const inCart = cart?.has(beatId, "mp3");

  const loadBeat = async () => {
    if (beat) return beat;
    const { data } = await api.get(`/beats/${beatId}`);
    setBeat(data);
    return data;
  };

  const openModal = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const b = await loadBeat();
      onClose?.();
      setTimeout(() => openLicense(b), 150);
    } catch {
      toast.error("That beat is no longer available");
    } finally {
      setBusy(false);
    }
  };

  const previewPlay = async (e) => {
    e.stopPropagation();
    if (busy) return;
    if (isActive) {
      toggle();
      return;
    }
    setBusy(true);
    try {
      const b = await loadBeat();
      playBeat(b);
    } catch {
      toast.error("Preview unavailable");
    } finally {
      setBusy(false);
    }
  };

  const quickAdd = async (e) => {
    e.stopPropagation();
    if (busy || !cart) return;
    if (inCart) {
      cart.removeItem(beatId, "mp3");
      return;
    }
    setBusy(true);
    try {
      const b = await loadBeat();
      const price = Number(b.prices?.mp3 || 0);
      if (!price) {
        toast.error("MP3 lease unavailable for this beat");
        return;
      }
      cart.addItem(beatToCartEntry(b, "mp3"));
      toast.success(`${b.title} added to cart`);
    } catch {
      toast.error("Couldn't add — try again");
    } finally {
      setBusy(false);
    }
  };

  const Icon = busy && !isActive ? Loader2 : isActive && playing ? Pause : isActive ? Play : Play;

  return (
    <span
      className="my-1 inline-flex items-center overflow-hidden rounded-full border border-emerald-300/40 bg-emerald-300/5 text-xs font-semibold text-emerald-200"
      data-testid={`chat-beat-${beatId}`}
    >
      <button
        type="button"
        onClick={previewPlay}
        disabled={busy}
        data-testid={`chat-beat-preview-${beatId}`}
        aria-label={isActive && playing ? "Pause preview" : "Play preview"}
        className="flex h-7 w-7 items-center justify-center border-r border-emerald-300/30 text-emerald-200 transition-colors hover:bg-emerald-300/15 disabled:opacity-50"
      >
        <Icon size={13} className={busy && !isActive ? "animate-spin" : ""} />
      </button>
      <button
        type="button"
        onClick={openModal}
        disabled={busy}
        data-testid={`chat-beat-license-${beatId}`}
        className="flex items-center gap-1.5 px-3 py-1 transition-colors hover:bg-emerald-300/15 disabled:opacity-50"
      >
        <Music size={12} />
        {title}
        <span className="font-mono text-[9px] uppercase tracking-wider text-emerald-400">· License</span>
      </button>
      {cart && (
        <button
          type="button"
          onClick={quickAdd}
          disabled={busy}
          data-testid={`chat-beat-cart-${beatId}`}
          aria-label={inCart ? "Remove from cart" : "Add MP3 lease to cart"}
          className={`flex h-7 w-7 items-center justify-center border-l border-emerald-300/30 transition-colors ${
            inCart ? "bg-emerald-300 text-black" : "text-emerald-200 hover:bg-emerald-300/15"
          } disabled:opacity-50`}
        >
          <Plus size={13} />
        </button>
      )}
    </span>
  );
};

const SuggestedBeatMini = ({ beat, onClose }) => {
  const { openLicense, playBeat, toggle, isCurrent, playing } = usePlayer();
  const cart = useCart();
  const active = isCurrent(beat);
  const Icon = active && playing ? Pause : Play;
  const inCart = cart?.has(beat.id, "mp3");
  const price = Number(beat.prices?.mp3 || 0);
  return (
    <div
      className="flex w-44 shrink-0 flex-col gap-2 rounded-2xl border border-white/10 bg-[#0B0B0E] p-2.5"
      data-testid={`chat-suggest-${beat.id}`}
    >
      <div className="relative overflow-hidden rounded-xl">
        <img
          src={fileUrl(beat.cover_url)}
          alt={beat.title}
          loading="lazy"
          className="h-24 w-full object-cover"
        />
        <button
          type="button"
          onClick={() => (active ? toggle() : playBeat(beat))}
          data-testid={`chat-suggest-play-${beat.id}`}
          aria-label={active && playing ? "Pause" : "Play"}
          className="absolute bottom-1.5 right-1.5 flex h-7 w-7 items-center justify-center rounded-full bg-emerald-300 text-black shadow-md transition-colors hover:bg-emerald-200"
        >
          <Icon size={13} />
        </button>
      </div>
      <div className="min-w-0 px-1">
        <p className="truncate text-xs font-semibold text-white">{beat.title}</p>
        <p className="font-mono text-[9px] uppercase tracking-wider text-zinc-500">
          {beat.genre} · {beat.bpm} BPM
        </p>
      </div>
      <div className="flex gap-1">
        <button
          type="button"
          onClick={() => {
            onClose?.();
            setTimeout(() => openLicense(beat), 150);
          }}
          data-testid={`chat-suggest-license-${beat.id}`}
          className="flex-1 rounded-full border border-emerald-300/40 bg-emerald-300/5 px-2 py-1 text-[10px] font-bold uppercase tracking-wider text-emerald-200 transition-colors hover:border-emerald-300 hover:bg-emerald-300/15"
        >
          License
        </button>
        {cart && price > 0 && (
          <button
            type="button"
            onClick={() => {
              if (inCart) {
                cart.removeItem(beat.id, "mp3");
              } else {
                cart.addItem(beatToCartEntry(beat, "mp3"));
                toast.success(`${beat.title} added to cart`);
              }
            }}
            data-testid={`chat-suggest-cart-${beat.id}`}
            aria-label={inCart ? "Remove from cart" : "Add to cart"}
            className={`flex h-7 w-7 items-center justify-center rounded-full border transition-colors ${
              inCart
                ? "border-emerald-300 bg-emerald-300 text-black"
                : "border-emerald-300/40 bg-emerald-300/5 text-emerald-200 hover:border-emerald-300"
            }`}
          >
            <Plus size={13} />
          </button>
        )}
      </div>
    </div>
  );
};

const SuggestedBeatsRow = ({ beats, onNavigate }) => (
  <div className="mt-3" data-testid="chat-suggest-row">
    <p className="mb-2 font-mono text-[10px] uppercase tracking-widest text-emerald-300">More like this</p>
    <div className="flex gap-2 overflow-x-auto pb-1">
      {beats.map((b) => (
        <SuggestedBeatMini key={b.id} beat={b} onClose={onNavigate} />
      ))}
    </div>
  </div>
);

const renderWithLinks = (text, onNavigate) => {
  const parts = [];
  let last = 0;
  let m;
  let idx = 0;
  TOKEN.lastIndex = 0;
  while ((m = TOKEN.exec(text)) !== null) {
    if (m.index > last) parts.push(text.slice(last, m.index));
    if (m[1]) {
      const inner = m[1].slice(7, -2); // strip [[beat: and ]]
      const [beatId, title] = inner.split("|");
      parts.push(<BeatChip key={`b-${idx++}`} beatId={beatId.trim()} title={title.trim()} onClose={onNavigate} />);
    } else if (m[2]) {
      const link = /^\[([^\]]+)\]\((\/[\w\-/]*)\)$/.exec(m[2]);
      if (link) {
        parts.push(
          <Link
            key={`l-${idx++}`}
            to={link[2]}
            onClick={onNavigate}
            data-testid={`chat-link-${link[2].replace(/\//g, "-").replace(/^-/, "")}`}
            className="font-semibold text-emerald-300 underline underline-offset-2 hover:text-emerald-200"
          >
            {link[1]}
          </Link>
        );
      }
    }
    last = m.index + m[0].length;
  }
  if (last < text.length) parts.push(text.slice(last));
  return parts.length ? parts : text;
};

const LeadCapture = ({ topic, label, onSubmitted }) => {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim() || busy) return;
    setBusy(true);
    try {
      await api.post("/contact", {
        name: `Chat lead — ${label}`,
        email: email.trim(),
        message: `Lead captured from chat concierge about ${label}. Session: ${getSid()}. Follow up with pricing + next step.`,
      });
      setDone(true);
      onSubmitted?.();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Couldn't send — try again");
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <div
        className="rounded-2xl border border-emerald-300/30 bg-emerald-300/5 px-4 py-3 text-xs text-emerald-200"
        data-testid={`chat-lead-success-${topic}`}
      >
        Got it — you'll hear back about {label} within 24h.
      </div>
    );
  }

  return (
    <form
      onSubmit={submit}
      className="rounded-2xl border border-emerald-300/30 bg-emerald-300/5 p-3"
      data-testid={`chat-lead-form-${topic}`}
    >
      <p className="mb-2 text-[11px] text-emerald-200">
        Want me to follow up on {label}? Drop your email.
      </p>
      <div className="flex gap-2">
        <input
          type="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="you@email.com"
          data-testid={`chat-lead-email-${topic}`}
          className="flex-1 rounded-full border border-white/10 bg-[#0B0B0E] px-3 py-1.5 text-xs text-white placeholder-zinc-600 outline-none focus:border-emerald-300/60"
        />
        <button
          type="submit"
          disabled={busy}
          data-testid={`chat-lead-submit-${topic}`}
          className="rounded-full bg-emerald-300 px-3 py-1.5 text-[11px] font-bold uppercase tracking-wider text-black hover:bg-emerald-200 disabled:opacity-50"
        >
          {busy ? "Sending…" : "Send"}
        </button>
      </div>
    </form>
  );
};

const CartDrawer = ({ onClose }) => {
  const cart = useCart();
  const [busy, setBusy] = useState(false);
  const checkout = async () => {
    if (busy || cart.items.length === 0) return;
    setBusy(true);
    try {
      const { data } = await api.post("/payments/checkout-cart", {
        items: cart.items.map((i) => ({ beat_id: i.beat_id, tier: i.tier })),
        origin_url: window.location.origin,
      });
      if (data?.checkout_url) {
        cart.clear();
        window.location.href = data.checkout_url;
      } else {
        toast.error("Checkout failed — try again");
      }
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Checkout failed");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div
      className="absolute inset-0 z-10 flex flex-col bg-[#141419]"
      data-testid="chat-cart-drawer"
    >
      <div className="flex items-center justify-between border-b border-white/10 px-5 py-4">
        <div>
          <p className="font-heading text-sm font-bold text-white">Your cart</p>
          <p className="font-mono text-[10px] uppercase tracking-widest text-emerald-300">
            {cart.items.length} beat{cart.items.length === 1 ? "" : "s"}
          </p>
        </div>
        <button onClick={onClose} data-testid="chat-cart-close" className="text-zinc-500 hover:text-white">
          <X size={17} />
        </button>
      </div>
      <div className="flex-1 overflow-y-auto p-4" data-testid="chat-cart-items">
        {cart.items.length === 0 ? (
          <div className="flex h-full items-center justify-center text-center text-xs text-zinc-500">
            Your cart is empty. Tap the + button next to a beat to add it.
          </div>
        ) : (
          <ul className="space-y-2">
            {cart.items.map((it) => (
              <li
                key={`${it.beat_id}-${it.tier}`}
                className="flex items-center gap-3 rounded-2xl border border-white/10 bg-[#0B0B0E] p-2.5"
                data-testid={`chat-cart-row-${it.beat_id}-${it.tier}`}
              >
                <img
                  src={fileUrl(it.cover_url)}
                  alt={it.title}
                  loading="lazy"
                  className="h-10 w-10 shrink-0 rounded-lg object-cover"
                />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-xs font-semibold text-white">{it.title}</p>
                  <div
                    className="mt-1 inline-flex overflow-hidden rounded-full border border-white/10"
                    data-testid={`chat-cart-tier-switch-${it.beat_id}`}
                  >
                    {["mp3", "wav"].map((t) => {
                      const price = Number(it.prices?.[t] || 0);
                      const active = it.tier === t;
                      const disabled = !price || active;
                      return (
                        <button
                          key={t}
                          type="button"
                          disabled={disabled && !active}
                          onClick={() => !active && price && cart.switchTier(it.beat_id, t)}
                          data-testid={`chat-cart-tier-${it.beat_id}-${t}`}
                          className={`px-2 py-0.5 font-mono text-[9px] uppercase tracking-wider transition-colors ${
                            active
                              ? "bg-emerald-300 text-black"
                              : price
                              ? "text-zinc-400 hover:bg-emerald-300/10 hover:text-emerald-200"
                              : "text-zinc-700"
                          }`}
                        >
                          {t === "mp3" ? "MP3" : "Premium"}
                        </button>
                      );
                    })}
                  </div>
                </div>
                <p className="font-mono text-xs font-bold text-emerald-200">${it.price.toFixed(2)}</p>
                <button
                  type="button"
                  onClick={() => cart.removeItem(it.beat_id, it.tier)}
                  data-testid={`chat-cart-remove-${it.beat_id}-${it.tier}`}
                  aria-label="Remove"
                  className="text-zinc-500 hover:text-rose-400"
                >
                  <Trash2 size={14} />
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
      <div className="border-t border-white/10 p-4" data-testid="chat-cart-footer">
        <div className="mb-2 flex items-center justify-between">
          <span className="font-mono text-[11px] uppercase tracking-widest text-zinc-400">Total</span>
          <span className="font-heading text-lg font-bold text-white" data-testid="chat-cart-total">
            ${cart.total.toFixed(2)}
          </span>
        </div>
        {cart.signedIn ? (
          <p className="mb-3 text-[10px] text-emerald-300/80" data-testid="chat-cart-synced">
            ✓ Saved to your account — picks up on any device you sign in on.
          </p>
        ) : (
          <p className="mb-3 text-[10px] text-zinc-500" data-testid="chat-cart-local-only">
            Sign in on the Account page to save this cart across devices.
          </p>
        )}
        <button
          type="button"
          onClick={checkout}
          disabled={busy || cart.items.length === 0}
          data-testid="chat-cart-checkout"
          className="flex w-full items-center justify-center gap-2 rounded-full bg-emerald-300 py-2.5 text-xs font-bold uppercase tracking-wider text-black transition-colors hover:bg-emerald-200 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {busy ? <Loader2 size={14} className="animate-spin" /> : <ShoppingCart size={14} />}
          {busy ? "Starting checkout…" : "Checkout"}
        </button>
      </div>
    </div>
  );
};

export const ChatWidget = () => {
  const [open, setOpen] = useState(false);
  const [cartOpen, setCartOpen] = useState(false);
  const cart = useChatCart();
  const [messages, setMessages] = useState([
    { role: "assistant", content: "Yo — ask me anything about leases, services, or booking the studio." },
  ]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [lead, setLead] = useState(null);
  const [suggestions, setSuggestions] = useState({}); // { assistantIdx: [beat, beat, beat] }
  const bottomRef = useRef(null);
  const showQuickReplies = messages.length === 1 && !streaming;

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, open, lead, suggestions]);

  const sendText = async (text) => {
    if (!text || streaming) return;
    const detected = detectLeadTopic(text);
    const beatIntent = BEAT_INTENT.test(text);
    setMessages((m) => [...m, { role: "user", content: text }, { role: "assistant", content: "" }]);
    setStreaming(true);
    let finalLen = 0;
    let finalReply = "";
    try {
      const resp = await fetch(`${API}/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: getSid(), message: text }),
      });
      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop();
        for (const part of parts) {
          if (!part.startsWith("data: ")) continue;
          const payload = JSON.parse(part.slice(6));
          if (payload.delta) {
            setMessages((m) => {
              const copy = [...m];
              const prev = copy[copy.length - 1].content;
              finalReply = prev + payload.delta;
              copy[copy.length - 1] = { role: "assistant", content: finalReply };
              finalLen = copy.length;
              return copy;
            });
          }
        }
      }
    } catch {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { role: "assistant", content: "Something glitched — try again in a sec." };
        finalLen = copy.length;
        return copy;
      });
    } finally {
      setStreaming(false);
      const assistantIdx = finalLen - 1;
      if (detected && (!lead || lead.topic !== detected.topic)) {
        setLead({ topic: detected.topic, label: detected.label, afterIdx: assistantIdx });
      }
      if (beatIntent && finalReply) {
        try {
          const exclude = extractBeatIds(finalReply).join(",");
          const { data } = await api.get("/beats/suggest", {
            params: { q: text, exclude, limit: 3 },
          });
          if (Array.isArray(data) && data.length) {
            setSuggestions((s) => ({ ...s, [assistantIdx]: data }));
          }
        } catch {
          /* non-fatal */
        }
      }
    }
  };

  // Allow other components (e.g. home FAQ carousel) to open the chat with a preloaded question.
  useEffect(() => {
    const onOpenChat = (ev) => {
      setOpen(true);
      const q = ev.detail?.question?.trim();
      if (q) setTimeout(() => sendText(q), 300);
    };
    window.addEventListener("prodrushi:open-chat", onOpenChat);
    return () => window.removeEventListener("prodrushi:open-chat", onOpenChat);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [streaming]);

  const send = (e) => {
    e.preventDefault();
    const text = input.trim();
    setInput("");
    sendText(text);
  };

  const onLinkNavigate = () => setOpen(false);

  return (
    <>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 24, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.96 }}
            transition={{ type: "spring", damping: 26, stiffness: 300 }}
            className="fixed bottom-40 right-4 z-[70] flex h-[26rem] w-[calc(100vw-2rem)] max-w-sm flex-col overflow-hidden rounded-3xl border border-white/10 bg-[#141419] shadow-2xl"
            data-testid="chat-panel"
          >
            <CartCtx.Provider value={cart}>
            <div className="flex items-center justify-between border-b border-white/10 px-5 py-4">
              <div>
                <p className="font-heading text-sm font-bold text-white">PRODRUSHI Assistant</p>
                <p className="font-mono text-[10px] uppercase tracking-widest text-emerald-300">AI · Online</p>
              </div>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => setCartOpen(true)}
                  data-testid="chat-cart-open"
                  aria-label="Open cart"
                  className="relative flex h-8 w-8 items-center justify-center rounded-full text-emerald-200 transition-colors hover:bg-emerald-300/10"
                >
                  <ShoppingCart size={15} />
                  {cart.items.length > 0 && (
                    <span
                      data-testid="chat-cart-badge"
                      className="absolute -top-0.5 -right-0.5 flex h-4 min-w-[1rem] items-center justify-center rounded-full bg-emerald-300 px-1 text-[9px] font-bold text-black"
                    >
                      {cart.items.length}
                    </span>
                  )}
                </button>
                <button onClick={() => setOpen(false)} data-testid="chat-close-button" className="text-zinc-500 hover:text-white">
                  <X size={17} />
                </button>
              </div>
            </div>
            <div className="flex-1 space-y-3 overflow-y-auto p-4" data-testid="chat-messages">
              {messages.map((m, i) => (
                <div key={i}>
                  <div className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                    <div
                      className={`max-w-[80%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed ${
                        m.role === "user" ? "bg-emerald-300 text-black" : "bg-[#0B0B0E] text-zinc-300"
                      }`}
                    >
                      {m.content
                        ? m.role === "assistant"
                          ? renderWithLinks(m.content, onLinkNavigate)
                          : m.content
                        : streaming && i === messages.length - 1
                        ? "…"
                        : ""}
                    </div>
                  </div>
                  {lead && lead.afterIdx === i && !streaming && (
                    <div className="mt-3">
                      <LeadCapture
                        topic={lead.topic}
                        label={lead.label}
                        onSubmitted={() => setLead((l) => (l ? { ...l, submitted: true } : l))}
                      />
                    </div>
                  )}
                  {suggestions[i] && suggestions[i].length > 0 && !streaming && (
                    <SuggestedBeatsRow beats={suggestions[i]} onNavigate={onLinkNavigate} />
                  )}
                </div>
              ))}
              <div ref={bottomRef} />
            </div>
            {showQuickReplies && (
              <div className="flex flex-wrap gap-1.5 border-t border-white/10 px-4 py-3" data-testid="chat-quick-replies">
                {QUICK_PROMPTS.map((q) => (
                  <button
                    key={q.label}
                    type="button"
                    onClick={() => sendText(q.prompt)}
                    data-testid={`chat-quick-${q.label.toLowerCase().replace(/\s+/g, "-")}`}
                    className="rounded-full border border-emerald-300/30 bg-emerald-300/5 px-3 py-1 text-[11px] font-medium text-emerald-200 transition-colors hover:border-emerald-300/60 hover:bg-emerald-300/10"
                  >
                    {q.label}
                  </button>
                ))}
              </div>
            )}
            <form onSubmit={send} className="flex gap-2 border-t border-white/10 p-3">
              <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Ask about leases, booking…"
                data-testid="chat-input"
                className="flex-1 rounded-full border border-white/10 bg-[#0B0B0E] px-4 py-2.5 text-sm text-white placeholder-zinc-600 outline-none focus:border-emerald-300/60"
              />
              <button type="submit" disabled={streaming} data-testid="chat-send-button"
                className="flex h-10 w-10 items-center justify-center rounded-full bg-emerald-300 text-black hover:bg-emerald-200 disabled:opacity-50">
                {streaming ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />}
              </button>
            </form>
            {cartOpen && <CartDrawer onClose={() => setCartOpen(false)} />}
            </CartCtx.Provider>
          </motion.div>
        )}
      </AnimatePresence>

      <motion.button
        onClick={() => setOpen((o) => !o)}
        data-testid="chat-toggle-button"
        whileHover={{ scale: 1.08 }}
        whileTap={{ scale: 0.94 }}
        className="fixed bottom-28 right-4 z-[70] flex h-13 w-13 items-center justify-center rounded-full bg-emerald-300 p-3.5 text-black shadow-[0_0_30px_rgba(110,231,183,0.4)]"
      >
        {open ? <X size={20} /> : <MessageCircle size={20} />}
      </motion.button>
    </>
  );
};
